import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  ModalContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useErrorHandler,
  useUploadFile,
} from '@sandtech-hub-monorepo/ui-kit';
import { ReactComponent as CertificateIcon } from '../../assets/Icons/certificate.svg';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { BulbIcon } from '@sandtech-hub-monorepo/ui-kit-site';
import {
  CurrentGuardianshipOptions,
  LocationDescriptionOptions,
  LivingCircumstancesOptions,
  DisabilityOptions,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { HumanitarianStatusFields } from './HumanitarianStatusFields';
import {
  Disability,
  Guardianship,
  HumanitarianStatus,
  HumanitarianStatusType,
  LivingCircumstances,
  LocationDescription,
  UpdateRefugeeCardDocument,
  UpdateUserDocument,
  UploadScope,
} from '@sandtech-hub-monorepo/ehub-client';
import { useMutation } from '@apollo/client';

const formSchema = z
  .object({
    guardianship: z.nativeEnum(Guardianship).refine((val) => val !== null, {
      message: 'Please select your current guardianship',
    }),
    locationDescription: z
      .nativeEnum(LocationDescription)
      .refine((val) => val !== null, {
        message: 'Please select a location description',
      }),
    livingCircumstances: z
      .nativeEnum(LivingCircumstances)
      .refine((val) => val !== null, {
        message: 'Please select your living circumstances',
      }),
    householdMonthlyIncomeUSD: z
      .string()
      .min(1, 'Please enter your household monthly income'),
    disability: z
      .nativeEnum(Disability)
      .refine((val) => val !== null && val !== undefined, {
        message: 'Please select a disability option',
      }),
    humanitarianStatus: z
      .nativeEnum(HumanitarianStatus)
      .refine((val) => val !== null, {
        message: 'Please select a humanitarian status option',
      }),
    humanitarianStatusType: z.nativeEnum(HumanitarianStatusType).nullable(),
  })
  .refine(
    (data) => {
      // If humanitarian status is Yes, then humanitarianStatusType is required
      if (data.humanitarianStatus === HumanitarianStatus.Yes) {
        return data.humanitarianStatusType !== null;
      }
      return true;
    },
    {
      message: 'Please select the type of humanitarian status',
      path: ['humanitarianStatusType'],
    }
  );

interface Props {
  onComplete: () => void;
  defaultValues?: {
    guardianship?: Guardianship | null;
    locationDescription?: LocationDescription | null;
    livingCircumstances?: LivingCircumstances | null;
    householdMonthlyIncomeUSD?: string | null;
    disability?: Disability | null;
    humanitarianStatus?: HumanitarianStatus | null;
    humanitarianStatusType?: HumanitarianStatusType | null;
    refugeeCard?: string | null;
  };
  completionLabel: string;
}

export const DiversityAndHumanitarianModal = ({
  onComplete,
  defaultValues,
  completionLabel,
}: Props) => {
  const { handleApolloError } = useErrorHandler();
  const [selectedFile, setSelectedFile] = useState<File>();
  const { uploadFile, loading: uploadFileLoading } = useUploadFile();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      guardianship: defaultValues?.guardianship || undefined,
      locationDescription: defaultValues?.locationDescription || undefined,
      livingCircumstances: defaultValues?.livingCircumstances || undefined,
      householdMonthlyIncomeUSD: defaultValues?.householdMonthlyIncomeUSD || '',
      disability: defaultValues?.disability || undefined,
      humanitarianStatus: defaultValues?.humanitarianStatus || undefined,
      humanitarianStatusType: defaultValues?.humanitarianStatusType || null,
    },
  });

  const [updateUser, { loading: updatingUser }] = useMutation(
    UpdateUserDocument,
    {
      onError: handleApolloError,
    }
  );

  const [updateRefugeeCard, { loading: updateRefugeeCardLoading }] =
    useMutation(UpdateRefugeeCardDocument, {
      onError: handleApolloError,
    });

  const loading = updatingUser || uploadFileLoading || updateRefugeeCardLoading;

  const submit = async () => {
    const values = form.getValues();

    // Upload refugee card if selected
    if (selectedFile) {
      const url = await uploadFile(UploadScope.RefugeeCard, selectedFile);
      if (url) {
        await updateRefugeeCard({
          variables: {
            input: {
              url,
            },
          },
        });
      }
    }

    // Prepare user input
    const input: any = {
      guardianship: values.guardianship,
      locationDescription: values.locationDescription,
      livingCircumstances: values.livingCircumstances,
      disability: values.disability,
      humanitarianStatus: values.humanitarianStatus,
      humanitarianStatusType: values.humanitarianStatusType,
    };

    // Handle household income parsing
    if (isNaN(parseFloat(values.householdMonthlyIncomeUSD as string))) {
      delete input.householdMonthlyIncomeUSD;
    } else {
      input.householdMonthlyIncomeUSD = parseFloat(
        values.householdMonthlyIncomeUSD as string
      );
    }

    // Update user information
    await updateUser({
      variables: { input },
    });

    // Complete - parent will handle enrollment
    onComplete();
  };

  return (
    <ModalContent>
      <div className="flex flex-col items-center text-center gap-2 max-w-screen-xs mx-auto">
        <div className="p-6 bg-primary/20 rounded-full text-primary mx-auto mb-2">
          <CertificateIcon height="48" width="48" />
        </div>
        <h2 className="text-primary font-semibold sm:text-2xl text-xl">
          Diversity & Humanitarian Status
        </h2>
      </div>
      <div className="flex flex-col gap-6 max-w-screen-xs mx-auto p-3">
        <div className="bg-primary/10 p-2.5 rounded-md flex gap-3 items-center">
          <div className="p-2 text-primary">
            <BulbIcon />
          </div>
          <div className="text-primary text-sm">
            Your responses help us understand diverse learner backgrounds. This
            info is only for reporting and won’t affect your application, see
            our Privacy Policy.
          </div>
        </div>
        <Form {...form}>
          <div className="flex flex-col gap-3">
            {/* Diversity and Inclusion Fields */}
            <FormField
              control={form.control}
              name="guardianship"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Your current guardianship
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={(field.value as string) || undefined}
                  >
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue placeholder="Please select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(Guardianship).map((item) => (
                        <SelectItem value={item} key={item}>
                          {CurrentGuardianshipOptions[item]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="locationDescription"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Description of the location you live in
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={(field.value as string) || undefined}
                  >
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue placeholder="Please select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(LocationDescription).map((item) => (
                        <SelectItem value={item} key={item}>
                          {LocationDescriptionOptions[item]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="livingCircumstances"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Your current living circumstances
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={(field.value as string) || undefined}
                  >
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue placeholder="Please select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(LivingCircumstances).map((item) => (
                        <SelectItem value={item} key={item}>
                          {LivingCircumstancesOptions[item]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="householdMonthlyIncomeUSD"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Your household monthly income
                  </FormLabel>
                  <FormControl>
                    <Input
                      startAdornment="$"
                      {...field}
                      value={field.value as string | undefined}
                      placeholder="0"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="disability"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm">
                    Do you have a disability?
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={(field.value as string) || undefined}
                  >
                    <FormControl>
                      <SelectTrigger ref={field.ref}>
                        <SelectValue placeholder="Please select" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="md:max-w-[618px]">
                      {Object.values(Disability).map((item) => (
                        <SelectItem value={item} key={item}>
                          {DisabilityOptions[item]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Humanitarian Status Fields */}
            <HumanitarianStatusFields
              control={form.control}
              watch={form.watch}
              selectedFile={selectedFile}
              onFileChange={setSelectedFile}
              showValidation={true}
            />
          </div>
        </Form>
      </div>
      <Button
        className="mx-auto my-8"
        onClick={submit}
        disabled={
          loading ||
          !form.formState.isValid ||
          (form.watch('humanitarianStatusType') ===
            HumanitarianStatusType.Refugee &&
            !selectedFile &&
            !defaultValues?.refugeeCard)
        }
      >
        {loading ? (
          <>
            <Loader2Icon className="animate-spin" /> Submitting...
          </>
        ) : (
          completionLabel
        )}
      </Button>
    </ModalContent>
  );
};
